// Reine Hilfsfunktionen für den WebRTC-Stream-Aufbau (frei von DOM/Socket-Abhängigkeiten).

/**
 * Führt fn aus und wiederholt bei Fehler mit exponentiellem Backoff.
 * @param {() => Promise<any>} fn
 * @param {{retries?: number, base?: number, max?: number, label?: string}} opts
 */
export async function withRetry(fn, { retries = 5, base = 500, max = 8000, label = 'op' } = {}) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (err) {
      attempt++;
      if (attempt > retries) {
        console.error(`❌ ${label}: nach ${retries} Versuchen aufgegeben:`, err?.message || err);
        throw err;
      }
      const delay = Math.min(base * 2 ** (attempt - 1), max);
      console.warn(`🔁 ${label}: Versuch ${attempt} fehlgeschlagen, retry in ${delay}ms (${err?.message || err})`);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
}

/**
 * Entscheidet, ob ein getUserMedia-Fehler transient ist (erneuter Versuch lohnt)
 * oder dauerhaft (Retry sinnlos). Transient = Kamera kurz belegt / abgebrochen;
 * dauerhaft = Berechtigung verweigert oder gar keine Kamera vorhanden.
 * @param {{name?: string}|null|undefined} err
 * @returns {boolean}
 */
export function isTransientCameraError(err) {
  if (!err || !err.name) return false;
  return err.name === 'NotReadableError' || err.name === 'AbortError';
}

/**
 * Entscheidet, ob ein Video-Track wiederhergestellt werden soll: nur wenn er
 * unerwartet beendet wurde (nicht durch bewusstes Stoppen des Nutzers/Codes).
 * @param {{readyState?: string, intentional?: boolean}} state
 * @returns {boolean}
 */
export function shouldRecoverTrack({ readyState, intentional } = {}) {
  return readyState === 'ended' && !intentional;
}

/**
 * Wandelt einen Socket.IO-Emit mit Callback in ein Promise um.
 * Lehnt ab, wenn die Antwort ein {error}-Feld trägt oder ein Timeout greift.
 */
export function emitAsync(socket, event, payload, { timeout = 10000 } = {}) {
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      reject(new Error(`${event} timeout nach ${timeout}ms`));
    }, timeout);
    socket.emit(event, payload, (response) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (response && response.error) reject(new Error(response.error));
      else resolve(response);
    });
  });
}
